import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Activity, FileText, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

export default function TmdAssessmentDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/tmd-assessment/${id}`)
      .then((r) => setDoc(r.data))
      .catch((e) => toast.error(errMsg(e, "Failed to load")))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 text-[var(--pa-text-muted)] text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading TMD analysis…
        </div>
      </Layout>
    );
  }
  if (!doc) return <Layout><div className="text-sm text-[var(--pa-text-muted)]">Not found.</div></Layout>;

  const a = doc.analysis || {};
  const sev = a.severity || "—";
  const sevColor = sev === "severe" ? "text-red-400" : sev === "moderate" ? "text-amber-400" : "text-emerald-400";

  return (
    <Layout>
      <div className="kicker">Phase 1 · Diagnostics</div>
      <h1 data-testid="tmd-detail-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <Activity size={22} className="text-[var(--pa-gold)]" /> TMD Assessment
      </h1>
      <div className="mt-1 text-xs text-[var(--pa-text-dim)]">{new Date(doc.created_at).toLocaleString()}</div>

      <AiOutputDisclaimer className="mt-4" />

      {/* Diagnosis */}
      <Section title="Diagnosis & Severity">
        <div className="grid sm:grid-cols-3 gap-3" data-testid="tmd-diagnosis">
          <Card label="Severity"><span className={`font-semibold capitalize ${sevColor}`}>{sev}</span></Card>
          <Card label="Axis I">
            <div className="flex flex-wrap gap-1">
              {(a.diagnosis?.axis_i || []).map((x, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text)]">{x.replace(/_/g, " ")}</span>
              ))}
            </div>
          </Card>
          <Card label="Axis II flags">
            <div className="flex flex-wrap gap-1">
              {(a.diagnosis?.axis_ii_flags || []).length ? a.diagnosis.axis_ii_flags.map((x, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">{x.replace(/_/g, " ")}</span>
              )) : <span className="text-xs text-[var(--pa-text-dim)]">None</span>}
            </div>
          </Card>
        </div>
        {a.severity_rationale && (
          <p className="mt-3 text-sm text-[var(--pa-text-muted)] leading-relaxed">{a.severity_rationale}</p>
        )}
      </Section>

      {/* Imaging */}
      {a.imaging_recommendation && (
        <Section title="Imaging Recommendation">
          <Card label={`Modality: ${a.imaging_recommendation.modality || "—"}`}>
            <p className="text-sm text-[var(--pa-text-muted)]">{a.imaging_recommendation.rationale}</p>
          </Card>
        </Section>
      )}

      {/* Treatment pathway */}
      <Section title="Treatment Pathway">
        <div className="space-y-3" data-testid="tmd-pathway">
          {(a.treatment_pathway || []).map((step, i) => (
            <div key={i} className="border border-[var(--pa-border)] rounded-md p-4 bg-[var(--pa-bg-2)]">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[var(--pa-gold)] text-[var(--pa-bg)] text-xs font-bold flex items-center justify-center">{step.step}</span>
                <span className="font-semibold text-[var(--pa-text)]">{step.label}</span>
                {step.splint_type && step.splint_type !== "none" && (
                  <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-[var(--pa-gold)]/20 border border-[var(--pa-gold)]/40 text-[var(--pa-gold)]">{step.splint_type.replace(/_/g, " ")}</span>
                )}
              </div>
              {step.interventions?.length > 0 && (
                <ul className="mt-2 ml-8 text-sm text-[var(--pa-text-muted)] list-disc space-y-0.5">
                  {step.interventions.map((iv, j) => <li key={j}>{iv}</li>)}
                </ul>
              )}
              {step.splint_rationale && (
                <p className="mt-2 ml-8 text-xs text-[var(--pa-text-dim)] italic">{step.splint_rationale}</p>
              )}
              {step.referrals?.length > 0 && (
                <div className="mt-2 ml-8 space-y-1">
                  {step.referrals.map((r, j) => r.specialty !== "none" && (
                    <div key={j} className="text-xs"><span className="text-[var(--pa-gold)] font-medium">{r.specialty?.replace(/_/g, " ")}: </span><span className="text-[var(--pa-text-muted)]">{r.indication}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {a.prognosis && (
        <Section title="Prognosis">
          <p className="text-sm text-[var(--pa-text-muted)]">{a.prognosis}</p>
        </Section>
      )}

      {/* CDT codes */}
      {a.cdt_codes?.length > 0 && (
        <Section title="CDT Billing Codes">
          <div className="grid sm:grid-cols-2 gap-2" data-testid="tmd-cdt">
            {a.cdt_codes.map((c, i) => (
              <div key={i} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-3 py-2">
                <span className="text-[var(--pa-gold)] font-mono font-semibold">{c.code}</span>
                <span className="ml-2 text-[var(--pa-text-muted)]">{c.description}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {a.referral_letter_markdown && (
        <Section title="Referral Letter">
          <pre data-testid="tmd-referral" className="text-xs whitespace-pre-wrap bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4 text-[var(--pa-text-muted)] leading-relaxed font-sans">{a.referral_letter_markdown}</pre>
        </Section>
      )}

      {doc.patient_id && (
        <Link to={`/treatment-plan/new?patient_id=${doc.patient_id}`} data-testid="tmd-to-tx-plan"
          className="mt-8 inline-flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition">
          <FileText size={14} /> Synthesize into Treatment Plan <ArrowRight size={14} />
        </Link>
      )}
    </Layout>
  );
}

function Section({ title, children }) {
  return <section className="mt-7"><div className="kicker mb-3">{title}</div>{children}</section>;
}
function Card({ label, children }) {
  return (
    <div className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)] mb-1">{label}</div>
      <div className="text-sm text-[var(--pa-text)]">{children}</div>
    </div>
  );
}
